import { t } from "i18next"
import Breadcrumb from "../../../../core/common/Breadcrumb/breadcrumb"
import { useAuth } from "../../../../core/hooks/useAuth"
import { useHandleCourses } from "../../../../core/hooks/useHandleCourses"
import { useParams } from "react-router-dom"
import { ConfigProvider, Progress, Table, Tag } from "antd"
import { ColumnsType } from "antd/es/table"
import { useTranslation } from "react-i18next"
import { useEffect, useMemo, useState } from "react"
import { getTrackVideo } from "../../../../core/api/trackLMSApi"
import { extractTextAndUrls } from "../../../../core/common/ults/domParser"
import { useTrackList } from "../../../../core/hooks/useTrackVideo"

const StudentProgressDetail = () => {
    const { id } = useParams();
    const { moodleUser } = useAuth()
    const { courseContents, courseDetails } = useHandleCourses(id)
    const courseDetailData = courseDetails?.courses[0];
    const { t } = useTranslation();
    const { data: trackList, isLoading } = useTrackList(id, moodleUser);


    //  lấy toàn bộ modules
    const allModules = courseContents?.flatMap((section: any) => section.modules || []) || [];

    // modules hoàn thành
    const countCompletedModulesByType = (modname: string): number => {
        return allModules.filter(
            (module: any) => module.modname === modname && [1, 2].includes(module.completiondata?.state)
        ).length;
    };

    // tính tiến độ
    const getProgressPercent = (completed: number, total: number): number => {
        return total ? Math.round((completed / total) * 100) : 0;
    };

    // Tổng số bài quiz, assign
    const quizModules = allModules.filter((mod: any) => mod.modname === 'quiz' && mod.completion > 0);
    const assignModules = allModules.filter((mod: any) => mod.modname === 'assign' && mod.completion > 0);

    // Số bài quiz, assign hoàn thành
    const completedQuiz = countCompletedModulesByType("quiz");
    const completedAssign = countCompletedModulesByType("assign");

    // Tiến độ quiz
    const quizProgressPercent = getProgressPercent(completedQuiz, quizModules.length);

    // Tiến độ assign
    const assignProgressPercent = getProgressPercent(completedAssign, assignModules.length);

    // đếm các hoạt động khác: chỉ hiển thị Diễn đàn (forum)
    const countOtherModules = allModules.filter((mod: any) => mod.modname === 'forum' && mod.completion > 0);
    const completedOtherModules = countOtherModules.filter((mod: any) => [1, 2].includes(mod.completiondata?.state));
    const otherProgressPercent = getProgressPercent(completedOtherModules.length, countOtherModules.length);


    // tiến độ bài tập (quiz + assign)
    const exerciseProgressPercent = getProgressPercent(
        completedQuiz + completedAssign,
        quizModules.length + assignModules.length
    );

    // danh sách theo loại modules
    const formatModules = (modules: any[], group: string) =>
        modules.map((mod: any, index: number) => ({
            key: `${group}-${mod.modname}-${index}`,
            name: mod.name,
            status: [1, 2].includes(mod.completiondata?.state) ? t("progress.completed") : t("progress.notCompleted"),
            group,
        }));

    // danh sách theo các modules khác
    const formatOtherModules = (modules: any[]) =>
        modules.map((mod: any, index: number) => ({
            key: `other-${mod.modname}-${index}`,
            name: mod.name,
            status: [1, 2].includes(mod.completiondata?.state) ? t("progress.completed") : t("progress.notCompleted"),
        }));


    const quizFormatted = formatModules(quizModules, t("progress.quizGroup"));
    const assignFormatted = formatModules(assignModules, t("progress.assignmentGroup"));


    const normalizeUrl = (url: string) => {
        try {
            const u = new URL(decodeURIComponent(url));
            return u.origin + u.pathname;  // không lấy phần search (query)
        } catch {
            return url;
        }
    };

    const videoStatusMap = useMemo(() => {
        if (!trackList?.length) return {};

        return trackList
            .filter((item: any) => typeof item.videoid === "string" && item.videoid.startsWith("http"))
            .reduce((map: any, item: any) => {
                const cleanUrl = decodeURIComponent(item.videoid).replace(/\\/g, "");

                // Lấy phần nguyên
                const ts = Math.floor(Number(item.timestamp) || 0);
                const dur = Math.floor(Number(item.duration) || 0);

                let status = "notStarted";
                if (ts > 0 && ts < dur) {
                    status = "inProgress";
                } else if (ts >= dur && dur > 0) {
                    status = "completed";
                }

                const percent = dur > 0 ? Math.floor((ts / dur) * 100) : 0;

                map[cleanUrl] = { status, percent };
                return map;
            }, {});
    }, [trackList]);

    const getVideoInfoFromMap = (videoUrl: string) => {
        // Các key thử nghiệm để tăng khả năng match
        const tryKeys = [
            // nếu bạn có normalizeUrl khác (origin + pathname) bạn có thể thêm ở đây
            normalizeUrl(videoUrl), // (nếu normalizeUrl trả origin+pathname)
            decodeURIComponent(videoUrl).replace(/\\/g, ""), // giống bên videoStatusMap
            (decodeURIComponent(videoUrl).replace(/\\/g, "")).split("?")[0].split("#")[0].trim()
        ];

        let entry: any = null;
        for (const k of tryKeys) {
            if (!k) continue;
            entry = (videoStatusMap as any)[k];
            if (entry) break;
        }

        // Không tìm thấy => mặc định
        if (!entry) return { status: t("progress.notWatched"), percent: 0, raw: null };

        // Nếu entry là string (phiên bản cũ)
        if (typeof entry === "string") {
            const st = entry;
            const label = st === "completed" ? t("progress.watched") : st === "inProgress" ? t("progress.watching") : t("progress.notWatched");
            const percent = st === "completed" ? 100 : 0;
            return { status: label, percent };
        }

        // Nếu entry là object {status, percent}
        const st = entry.status;
        const label = st === "completed" ? t("progress.watched") : st === "inProgress" ? t("progress.watching") : t("progress.notWatched");
        return { status: label, percent: entry.percent || 0 };
    };

    const weekData = (courseContents || [])
        .filter((section: any) => section.section > 0)
        .map((section: any, index: number) => {
            const videos = section.modules?.flatMap((mod: any) => {
                const list: { name: string; url: string; status?: string; percent?: number }[] = [];

                const pushVideo = (name: string, url: string) => {
                    const info = getVideoInfoFromMap(url);
                    list.push({
                        name,
                        url,
                        status: info.status,
                        percent: info.percent,
                    });
                };

                if (mod.modname === "unilabel") {
                    const result = extractTextAndUrls(mod.description);
                    result?.forEach((res: any) => {
                        if (res.type === "video" || res.url?.includes(".mp4")) {
                            pushVideo(res.text, res.url);
                        }
                    });
                } else if (mod.modname === "resource") {
                    const fileUrl = mod.contents?.[0]?.fileurl;
                    if (fileUrl && fileUrl.includes(".mp4")) {
                        pushVideo(mod.name, fileUrl);
                    }
                } else if (mod.modname === "url") {
                    const externalUrl = mod.contents?.[0]?.fileurl || mod.externalurl;
                    if (externalUrl && (externalUrl.includes(".mp4") || externalUrl.includes("youtube") || externalUrl.includes("vimeo"))) {
                        pushVideo(mod.name, externalUrl);
                    }
                }
                return list;
            }) || [];

            return {
                key: section.id || index,
                name: section.name || `${t("progress.week")} ${index + 1}`,
                videos,
            };
        });

    const calculateWeekProgress = (videos: any[]) => {
        if (!videos || videos.length === 0) return 0;

        const total = videos.length;
        const completed = videos.filter((v: any) => v.status === t("progress.watched")).length;
        return Math.round((completed / total) * 100);
    };



    const totalWeeks = weekData.filter((week: any) => week.videos.length > 0).length;
    const completedWeeks = weekData.filter(
        (week: any) => calculateWeekProgress(week.videos) === 100
    ).length;

    const theoryProgressPercent = getProgressPercent(completedWeeks, totalWeeks);

    // group-title Bài tập
    const exerciseData = [
        {
            key: `group-${t("progress.quizGroup")}`,
            name: t("progress.quizGroup"),
            status: '',
            isGroup: true,
            progressPercent: quizProgressPercent,
        },
        ...quizFormatted,
        {
            key: `group-${t("progress.assignmentGroup")}`,
            name: t("progress.assignmentGroup"),
            status: '',
            isGroup: true,
            progressPercent: assignProgressPercent,
        },
        ...assignFormatted,
    ];


    // Colums table Bài tập + Hoạt động khác
    const columns: ColumnsType<any> = [
        {
            title: t("progress.activityName"),
            dataIndex: 'name',
            key: 'name',
            width: 400,
            render: (text, record: any) =>
                record.isGroup ? (
                    <div
                        style={{
                            backgroundColor: '#f0f2f5',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#1a1a1a',
                            padding: '6px 12px',
                            textTransform: 'uppercase',
                        }}
                    >
                        {record.name}
                    </div>
                ) : (
                    record.name
                ),
        },
        {
            title: t("progress.status"),
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: 200,
            render: (text, record: any) =>
                record.isGroup ? (
                    <div style={{ padding: '4px 8px' }}>
                        <Progress
                            percent={record.progressPercent || 0}
                            size="small"
                            strokeColor="#52c41a"
                        />
                    </div>
                ) : (
                    <Tag color={text === t("progress.completed") ? "green" : "red"}>{text}</Tag>
                ),
        },
    ];


    // Colums lý thuyết theo tuần
    const weekColumns: ColumnsType<any> = [
        {
            title: t("progress.week"),
            dataIndex: "name",
            key: "name",
            width: 400,
        },
        {
            title: t("progress.progress"),
            dataIndex: "progress",
            key: "progress",
            width: 200,
            align: "center",
            render: (_: any, record: any) => {
                if (!record.videos || record.videos.length === 0) {
                    return null; // hoặc return "" để không render gì
                }
                const percent = calculateWeekProgress(record.videos);
                return <Progress percent={percent} size="small" />;
            },
        },
    ];

    // Columns for inner table (videos) - explicitly typed to avoid TS inferring `never`
    const videoColumns: ColumnsType<any> = [
        { title: t("progress.videoName"), dataIndex: "name", key: "name", width: 400 },
        {
            title: t("progress.status"),
            dataIndex: "status",
            key: "status",
            width: 93,
            align: "center",
            render: (_: any, video: any) => {
                const watched = t("progress.watched");
                const watching = t("progress.watching");
                const color =
                    video.status === watched
                        ? "green"
                        : video.status === watching
                        ? "orange"
                        : "red";
                return (
                    <div className="d-flex" style={{ alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <Tag color={color} className="flex-column">{video.status}</Tag>
                    </div>
                );
            }
        },
        {
            title: t("progress.progress"),
            dataIndex: "status",
            key: "status",
            width: 93,
            align: "center",
            render: (_: any, video: any) => {
                const watched = t("progress.watched");
                const watching = t("progress.watching");
                const color =
                    video.status === watched
                        ? "green"
                        : video.status === watching
                        ? "orange"
                        : "red";
                return (
                    <div className="d-flex" style={{ alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <Progress
                            type="circle"
                            percent={video.percent || 0}
                            width={26}
                            strokeColor={color}
                            className="flex-column"
                        />
                    </div>
                );
            }
        }
    ];





    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    return (
        <ConfigProvider
            theme={{
                components: {
                    Table: {
                        headerBg: "#fafafa",          // Màu nền header
                        headerColor: "#000",          // Màu chữ header
                        cellPaddingBlock: 8,         // Padding dọc cell => gián tiếp tăng height
                        cellPaddingInline: 12,        // Padding ngang cell
                    },
                },
            }}
        >
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <Breadcrumb title={t("progress.title")} />
                    </div>
                    <h3 className="py-4">{courseDetailData?.fullname}</h3>

                    <div className="mb-5">
                        <h5 className="mb-2 d-flex justify-content-between">
                            <span>{t("progress.theory")} </span>
                            <Progress
                                percent={theoryProgressPercent}
                                style={{ width: '40%', height: 10 }}
                                strokeColor={
                                    theoryProgressPercent === 100
                                        ? "#52c41a" // 100% thì toàn bộ màu xanh
                                        : {
                                            "0%": "#ff4d4f",
                                            "40%": "#faad14",
                                            "90%": "#52c41a",
                                        }
                                }
                            />
                        </h5>
                        <Table
                            columns={weekColumns}
                            dataSource={weekData}
                            pagination={false}
                            bordered
                            scroll={{ x: 'max-content' }}

                            expandable={{
                                expandedRowKeys,
                                onExpand: (expanded, record) => {
                                    if (expanded) {
                                        setExpandedRowKeys([record.key]);
                                    } else {
                                        setExpandedRowKeys([]);
                                    }
                                },
                                expandedRowRender: (record) => (
                                    <Table<any>
                                        columns={videoColumns}
                                        dataSource={record.videos.map((v: any, i: any) => ({ key: i, ...v }))}
                                        pagination={false}
                                        size="small"
                                    />
                                ),
                                rowExpandable: (record) => record.videos.length > 0,
                            }}
                        />
                    </div>

                    <div className="mb-5">
                        <h5 className="mb-2 d-flex justify-content-between">
                            <span>{t("progress.exercises")}</span>
                            <Progress
                                percent={exerciseProgressPercent}
                                style={{ width: '40%' }}
                                strokeColor={
                                    exerciseProgressPercent === 100
                                        ? "#52c41a" // 100% thì toàn bộ màu xanh
                                        : {
                                            "0%": "#ff4d4f",
                                            "40%": "#faad14",
                                            "90%": "#52c41a",
                                        }
                                }
                            />
                        </h5>
                        <Table
                            columns={columns}
                            dataSource={exerciseData}
                            pagination={false}
                            bordered
                            scroll={{ x: 'max-content' }}
                        />
                    </div>

                    <div>
                        <h5 className="mb-2 d-flex justify-content-between">
                            <span>{t("progress.otherActivities")}</span>
                            <Progress
                                percent={otherProgressPercent}
                                style={{ width: '40%' }}
                                strokeColor={
                                    exerciseProgressPercent === 100
                                        ? "#52c41a" // 100% thì toàn bộ màu xanh
                                        : {
                                            "0%": "#ff4d4f",
                                            "40%": "#faad14",
                                            "90%": "#52c41a",
                                        }
                                } />
                        </h5>
                        <Table
                            columns={columns}
                            dataSource={formatOtherModules(countOtherModules)}
                            pagination={false}
                            bordered
                            scroll={{ x: 'max-content' }}
                        />
                    </div>
                </div>
            </div>
        </ConfigProvider>

    );
};

export default StudentProgressDetail;
